import {
  InformationCircleIcon,
  PhotoIcon,
  // MicrophoneIcon, // Remove MicrophoneIcon import if no longer used elsewhere in Field
} from '@heroicons/react/24/solid';
import { motion } from 'motion/react';
import { useEffect, useState, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { CreateSuggestions } from '../screens/onboarding/components/CreateSuggestions';
import { AudioRecorder } from './AudioRecorder'; // Keep AudioRecorder import

interface FieldProps {
  register: any;
  tooltip?: string;
  label: string;
  fieldName: string;
  watch?: any;
  options?: any;
  validate?: (value: any) => boolean;
  setValue?: UseFormSetValue<any>;
  useSuggestions?: boolean;
  type?: string;
  textError?: string;
  placeholder?: string;
  className?: string;
  enablePulseAnimation?: boolean;
  onImageUpload?: (file: File) => void;
  imagePreview?: string;
}

export const Field = ({
  register,
  tooltip,
  label,
  textError,
  fieldName,
  watch,
  options,
  validate,
  setValue,
  type = 'text',
  useSuggestions,
  placeholder,
  className,
  enablePulseAnimation = false,
  onImageUpload,
  imagePreview,
}: FieldProps) => {
  const field = watch && watch(fieldName);
  const [isValid, setIsValid] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (field && validate) {
      setIsValid(validate(field));
    } else if (!validate) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [field, validate]);

  // Callback function to update the field value
  const handleTranscriptionComplete = (suggestion: string) => {
    if (setValue && fieldName) {
      setValue(fieldName, suggestion);
    }
  };

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImageUpload) {
      onImageUpload(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // DaisyUI v5: input classes go on the wrapper, not the input itself
  const getInputWrapperClass = (extra = '') => {
    let base = 'input input-bordered flex items-center gap-2 w-full';
    if (!isValid && textError) base += ' input-error';
    if (isValid && !textError) base += ' input-success';
    if (extra) base += ` ${extra}`;
    return base;
  };

  // --- Label and description block ---
  const LabelBlock = (
    <div className="label flex items-center justify-between">
      <span className="label-text font-bold text-base-content">{label}</span>
      <div className="flex items-center space-x-2">
        {tooltip && (
          <div className="tooltip tooltip-left" data-tip={`${tooltip}`}>
            <InformationCircleIcon className="h-5 w-5" />
          </div>
        )}
        {useSuggestions && type === 'textarea' && (
          <>
            <AudioRecorder
              onTranscriptionComplete={handleTranscriptionComplete}
              enablePulseAnimation={enablePulseAnimation}
            />
            {setValue && (
              <div className="z-5 relative">
                <CreateSuggestions
                  fieldName={fieldName}
                  filledText={field}
                  setValue={setValue}
                  enablePulseHint={enablePulseAnimation}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // --- Input element block ---
  let InputBlock = null;
  if (type === 'textarea') {
    InputBlock = (
      <label className={getInputWrapperClass('mt-1 h-32 items-start')}>
        <textarea
          placeholder={placeholder}
          className="h-full grow resize-none border-0 bg-transparent focus:outline-none text-base-content"
          {...register(fieldName, {
            ...options,
            validate,
          })}
        />
      </label>
    );
  } else if (type === 'readonly') {
    InputBlock = (
      <label className={getInputWrapperClass('mt-1')}>
        <input
          type="text"
          placeholder={placeholder}
          className="grow border-0 bg-transparent focus:outline-none"
          {...register(fieldName, {
            ...options,
            validate,
          })}
          readOnly
        />
      </label>
    );
  } else if (type === 'select') {
    InputBlock = (
      <label className={getInputWrapperClass('mt-1')}>
        <select
          className="grow border-0 bg-transparent focus:outline-none"
          {...register(fieldName, {
            ...options,
            validate,
          })}
        >
          {options?.map((option: any) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  } else if (type === 'audio') {
    InputBlock = null; // AudioRecorder is handled in LabelBlock
  } else if (type === 'image') {
    InputBlock = (
      <div className="mt-1">
        <div
          onClick={handleImageClick}
          className="relative aspect-square bg-base-200 rounded-lg overflow-hidden border-2 border-dashed border-base-300 hover:border-base-content cursor-pointer transition-colors"
        >
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          ) : (
              <div className="flex flex-col items-center justify-center h-full text-base-content">
              <PhotoIcon className="h-12 w-12 mb-2" />
              <p className="text-sm text-center">Hacer clic para subir imagen</p>
            </div>
          )}
          {imagePreview && (
              <div className="absolute inset-0 bg-base-content bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center">
              <PhotoIcon className="h-8 w-8 text-base-100 opacity-0 hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
          {...register(fieldName, options)}
        />
      </div>
    );
  } else {
    InputBlock = (
      <label className={getInputWrapperClass('mt-1')}>
        <input
          type={type}
          placeholder={placeholder}
          className="grow border-0 bg-transparent focus:outline-none"
          {...register(fieldName, {
            ...options,
            validate,
          })}
        />
      </label>
    );
  }

  return (
    <motion.div
      key={fieldName}
      initial={{ x: -100 }}
      animate={{ x: 0 }}
      exit={{ x: 100 }}
      className={`form-control my-2 flex flex-col gap-2 ${
        className || ''
      }`}
    >
      {LabelBlock}
      {InputBlock}
      {!isValid && textError && (
        <label className="ml-2 mt-1 text-xs text-error">{textError}</label>
      )}
    </motion.div>
  );
};
